<template>
  <v-dialog :value="Boolean(state.current)" persistent :max-width="680" :retain-focus="true" :z-index="1000000" scrollable>
    <v-card v-if="state.current" rounded="xl">
      <v-card-title class="text-h6 pb-2 lead-confirmation__title">{{ conflicts.length ? "Lead-Zuordnung prüfen" : items.length === 1 ? "Vorhandenen Lead bearbeiten?" : "Vorhandene Leads bearbeiten?" }}</v-card-title>
      <v-card-text class="pb-2">
        <p v-if="conflicts.length">Der Termin ist gespeichert. Die neuen Angaben passen nicht eindeutig zum bisherigen Lead. Bitte entscheide, ob es dieselbe oder eine andere Person ist.</p>
        <p v-else>Der Termin ist gespeichert. Diese Änderungen werden nach deiner Bestätigung {{ items.length === 1 ? "im vorhandenen Lead" : "in den vorhandenen Leads" }} übernommen.</p>
        <section v-for="conflict in conflicts" :key="conflict.key" class="lead-confirmation__conflict">
          <div class="font-weight-bold mb-3">{{ appointmentLabel(conflict) }}</div>
          <table class="lead-confirmation__contacts">
            <caption class="sr-only">Bisherige und neue Angaben vergleichen</caption>
            <thead><tr><th scope="col">Angabe</th><th scope="col">Bisheriger Lead</th><th scope="col">Jetzt im Termin</th></tr></thead>
            <tbody>
              <tr v-for="field in contactFields" :key="field.key">
                <th scope="row">{{ field.label }}</th>
                <td>{{ conflict.before[field.key] || "Keine Angabe" }}</td>
                <td>{{ conflict.after[field.key] || "Keine Angabe" }}</td>
              </tr>
            </tbody>
          </table>
          <v-radio-group :value="choices[conflict.key]" :mandatory="false" :aria-label="'Lead-Zuordnung für ' + (conflict.before.name || 'diese Person')" hide-details class="mt-4" @change="$set(choices, conflict.key, $event)">
            <v-radio value="update" :disabled="!conflict.canUpdate" class="lead-confirmation__choice">
              <template #label>
                <div><strong>Dieselbe Person – bestehenden Lead aktualisieren</strong><div class="text-caption mt-1">Namen und neue Kontaktdaten übernehmen. Notizen, Herkunft und Verlauf bleiben erhalten.</div></div>
              </template>
            </v-radio>
            <p v-if="!conflict.canUpdate" class="text-caption ml-8 mb-3">Der bisherige Lead gehört bereits zu einem anderen Kundenaccount und kann hier nicht umgeschrieben werden.</p>
            <v-radio value="separate" class="lead-confirmation__choice">
              <template #label>
                <div><strong>{{ conflict.separateLeadExists ? "Andere Person – passenden Lead verwenden" : "Andere Person – neuen Lead erstellen" }}</strong><div class="text-caption mt-1">Bisherige Angaben und Notizen am alten Lead behalten. Den Termin dem {{ conflict.separateLeadExists ? "passenden" : "neuen" }} Lead zuordnen.</div></div>
              </template>
            </v-radio>
          </v-radio-group>
        </section>
        <div v-if="items.length" class="lead-confirmation__items">
          <strong v-if="conflicts.length" class="d-block mt-4">Weitere Lead-Änderungen</strong>
          <div v-for="item in items" :key="item.leadId" class="lead-confirmation__item">
            <strong class="d-block mb-1 lead-confirmation__name">{{ item.name }}</strong>
            <div v-if="item.from === item.to" class="text-caption mb-3">Lead-Status bleibt «{{ item.to }}».</div>
            <section v-for="section in itemSections(item)" :key="section.key" class="lead-confirmation__section">
              <div class="font-weight-bold mb-1">{{ section.title }}</div>
              <p v-if="section.context" class="text-caption mb-2">{{ section.context }}</p>
              <table v-if="section.rows.length" class="lead-confirmation__changes">
                <caption class="sr-only">{{ section.title }} für {{ item.name }}: bisherige und neue Werte</caption>
                <thead><tr><th scope="col">Angabe</th><th scope="col">Bisher im Lead</th><th scope="col">Nach Bestätigung</th></tr></thead>
                <tbody>
                  <tr v-for="row in section.rows" :key="row.key">
                    <th scope="row">{{ row.label }}</th>
                    <td data-label="Bisher im Lead">{{ row.before }}</td>
                    <td data-label="Nach Bestätigung" class="lead-confirmation__after"><strong>{{ row.after }}</strong></td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="text-caption mb-0">Die Personenzuordnung im Terminverlauf wird aktualisiert. Terminart, Datum und Uhrzeit bleiben gleich.</p>
            </section>
            <p v-if="!itemSections(item).length" class="text-caption mb-0">Für diese Änderung ist noch keine Detailvorschau verfügbar.</p>
          </div>
        </div>
        <p class="text-caption mt-4 mb-0">{{ conflicts.length ? "Bei Abbrechen bleiben die vorhandenen Leads unverändert. Für diese Zuordnung wird kein neuer Lead erstellt." : "Bei Abbrechen bleibt der Lead unverändert." }}</p>
      </v-card-text>
      <v-card-actions class="pa-4 pt-2 lead-confirmation__actions">
        <v-spacer />
        <v-btn text @click="finish(false)">Abbrechen</v-btn>
        <v-btn color="primary" depressed :disabled="!canConfirm" @click="finish(true)">{{ conflicts.length ? "Auswahl übernehmen" : items.length === 1 ? "Lead aktualisieren" : "Leads aktualisieren" }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { leadConfirmationState, finishLeadConfirmation } from "../services/leadConfirmation"

export default {
  name: "LeadChangeConfirmation",
  data: () => ({
    state: leadConfirmationState,
    choices: {},
    contactFields: [{ key: "name", label: "Name" }, { key: "phone", label: "Telefon" }, { key: "email", label: "E-Mail" }],
  }),
  computed: {
    items() { return this.state.current ? this.state.current.items : [] },
    conflicts() { return this.state.current ? this.state.current.conflicts : [] },
    canConfirm() {
      return this.conflicts.every((conflict) => this.choices[conflict.key] === "separate" || (conflict.canUpdate && this.choices[conflict.key] === "update"))
    },
  },
  watch: {
    "state.current"() { this.choices = {} },
  },
  methods: {
    finish(confirmed) { finishLeadConfirmation(confirmed, this.choices) },
    appointmentLabel(conflict) {
      const first = conflict.appointments[0]
      const date = first.date ? first.date.split("-").reverse().join(".") : ""
      return `Person ${first.participant} · ${date}${first.start ? `, ${first.start} Uhr` : ""}${conflict.appointments.length > 1 ? ` · ${conflict.appointments.length} Termine` : ""}`
    },
    itemSections(item) {
      const sections = []
      const changes = Array.isArray(item.changes) ? item.changes : item.from !== item.to
        ? [{ key: "status", label: "Lead-Status", before: item.from, after: item.to }] : []
      if (changes.length) sections.push({ key: "lead", title: "Angaben im Lead", rows: changes })
      for (const appointment of item.appointments || []) {
        const before = appointment.before
        const after = appointment.after
        const reference = before || after
        sections.push({
          key: appointment.key,
          title: !before ? "Termin zum Lead hinzufügen" : after.status === "removed" ? "Terminzuordnung entfernen" : "Termin im Lead aktualisieren",
          context: `${this.typeLabel(reference.type)} · ${this.dateLabel(reference.date)} · ${this.timeLabel(reference)}${appointment.participant === 2 ? " · Person 2" : ""}`,
          rows: this.appointmentRows(before, after),
        })
      }
      return sections
    },
    appointmentRows(before, after) {
      const values = (event) => event ? {
        type: this.typeLabel(event.type), date: this.dateLabel(event.date), time: this.timeLabel(event),
        status: this.statusLabel(event.status), personType: event.personType,
      } : {}
      const previous = values(before)
      const next = values(after)
      return [
        ["type", "Terminart"], ["date", "Datum"], ["time", "Uhrzeit"], ["status", "Terminstatus"], ["personType", "Person im Termin"],
      ].filter(([key]) => (key !== "personType" || before) && previous[key] !== next[key])
        .map(([key, label]) => ({ key, label, before: previous[key] || "—", after: next[key] || "—" }))
    },
    typeLabel(type) { return { consultation: "Beratungstermin", trial: "Probetraining", guestbook: "Gästebuch" }[type] || "Termin" },
    statusLabel(status) {
      return { planned: "Termin vereinbart", checkedin: "Eingecheckt", done: "Termin wahrgenommen", noshow: "Nicht gekommen", cancelled: "Termin abgesagt", removed: "Terminzuordnung entfernt" }[status] || status || "Keine Angabe"
    },
    dateLabel(date) { return /^\d{4}-\d{2}-\d{2}$/.test(date || "") ? date.split("-").reverse().join(".") : date || "Keine Angabe" },
    timeLabel(event) { return [event.start, event.end].filter(Boolean).join("–") || "Keine Angabe" },
  },
}
</script>

<style scoped>
.lead-confirmation__item { padding: 16px 0; border-bottom: 1px solid rgba(128, 128, 128, .18); }
.lead-confirmation__item:last-child { border-bottom: 0; }
.lead-confirmation__name { font-size: 1rem; overflow-wrap: anywhere; }
.lead-confirmation__section { margin-top: 16px; }
.lead-confirmation__changes { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: .8rem; }
.lead-confirmation__changes th, .lead-confirmation__changes td { padding: 10px 8px; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
.lead-confirmation__changes thead th { background: rgba(128, 128, 128, .08); font-weight: 500; }
.lead-confirmation__changes th:first-child { width: 24%; }
.lead-confirmation__changes tbody tr + tr { border-top: 1px solid rgba(128, 128, 128, .15); }
.lead-confirmation__after { background: rgba(0, 137, 123, .07); }
.lead-confirmation__title { word-break: normal; }
.lead-confirmation__conflict { padding: 16px 0; border-bottom: 1px solid rgba(128, 128, 128, .18); }
.lead-confirmation__contacts { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: .8rem; }
.lead-confirmation__contacts th, .lead-confirmation__contacts td { padding: 8px; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
.lead-confirmation__contacts thead th { background: rgba(128, 128, 128, .08); }
.lead-confirmation__contacts th:first-child { width: 20%; }
.lead-confirmation__contacts tbody tr + tr { border-top: 1px solid rgba(128, 128, 128, .12); }
.lead-confirmation__choice { align-items: flex-start; padding: 10px 0; min-height: 48px; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
@media (max-width: 480px) {
  .lead-confirmation__changes thead { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
  .lead-confirmation__changes tbody, .lead-confirmation__changes tr { display: block; }
  .lead-confirmation__changes tr { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .lead-confirmation__changes th:first-child { width: auto; grid-column: 1 / -1; padding-bottom: 4px; }
  .lead-confirmation__changes td::before { content: attr(data-label); display: block; font-size: .7rem; font-weight: 400; margin-bottom: 4px; }
  .lead-confirmation__actions { flex-wrap: wrap; gap: 8px; }
  .lead-confirmation__actions > .spacer { display: none; }
  .lead-confirmation__actions > .v-btn { flex: 1 1 100%; margin-left: 0 !important; }
}
</style>
